import { loadLeaflet } from './loader.js';
import { createMapClickHandler, createLoadingElement, createErrorElement } from './ui.js';
import { resolveMapPoints, getOffsetCoordinate } from './utils.js';
import { getCardComponent } from '../blocks/cards/registry.js';
import { h, render } from 'preact';
import htm from 'htm';

const html = htm.bind(h);

/**
 * Create a simple dot marker icon for points of interest.
 */
function buildIcon(L) {
  return L.divIcon({
    className: "mta-marker",
    html: '<span class="mta-marker__dot"></span>',
    iconSize: [22, 22],
    iconAnchor: [11, 11]
  });
}

export async function mountLeafletMap(container, payload, { t, config }, cancellationState) {
  let mapInstance = null;
  const closePopupFn = () => mapInstance?.closePopup();
  const handleMapClick = createMapClickHandler(config);
  container.addEventListener('click', handleMapClick);

  const loadingEl = createLoadingElement(t);
  container.appendChild(loadingEl);

  let L;
  try {
    L = await loadLeaflet();
  } catch (err) {
    if (loadingEl.parentNode === container) container.removeChild(loadingEl);
    const retryFn = () => {
      if (errorEl.parentNode === container) container.removeChild(errorEl);
      mountLeafletMap(container, payload, { t, config }, cancellationState);
    };
    const errorEl = createErrorElement(t, retryFn);
    container.appendChild(errorEl);
    return {
      destroy() {
        container.removeEventListener('click', handleMapClick);
        if (errorEl.parentNode === container) container.removeChild(errorEl);
      },
      update() { },
      invalidate() { }
    };
  }

  if (loadingEl.parentNode === container) container.removeChild(loadingEl);
  if (cancellationState.isDestroyed) {
    return {
      destroy() { container.removeEventListener('click', handleMapClick); },
      update() { },
      invalidate() { }
    };
  }

  const mapEl = document.createElement("div");
  mapEl.className = "mta-leaflet";
  mapEl.style.width = "100%";
  mapEl.style.height = "100%";
  mapEl.setAttribute("role", "application");
  mapEl.setAttribute("aria-label", "Interactive map");
  container.appendChild(mapEl);

  const map = L.map(mapEl, {
    zoomControl: true,
    scrollWheelZoom: true,
    keyboard: true
  });
  mapInstance = map;

  L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: "abcd",
    maxZoom: 20
  }).addTo(map);

  let markerGroup = L.layerGroup().addTo(map);
  let polylineObj = null;

  function renderPoints(data) {
    markerGroup.clearLayers();
    if (polylineObj) {
      map.removeLayer(polylineObj);
      polylineObj = null;
    }

    const resolved = resolveMapPoints(data);
    if (resolved.length === 0) {
      map.setView([20, 0], 2);
      return;
    }

    const pathCoords = [];
    let singleMarker = null;
    resolved.forEach(pt => {
      const { lat, lng } = getOffsetCoordinate(pt._resolvedLat, pt._resolvedLng, pt._duplicateIndex);
      const order = pt.order ?? pt.day ?? (pt._resolvedIndex + 1);
      const name = pt.name ?? pt.label ?? `Point ${order}`;
      const marker = L.marker([lat, lng], {
        icon: buildIcon(L),
        title: name,
        alt: name
      });

      const CardComponent = getCardComponent(pt.type);
      const popupContainer = document.createElement('div');
      render(html`<${CardComponent} item=${pt} ctx=${{ t, config }} variant="popup" onClose=${closePopupFn} />`, popupContainer);

      marker.bindPopup(popupContainer, {
        closeButton: false,
        maxWidth: 280,
        autoPanPaddingTopLeft: [50, 16],
        autoPanPaddingBottomRight: [16, 16]
      });
      marker.addTo(markerGroup);
      pathCoords.push([lat, lng]);
      
      if (resolved.length === 1) singleMarker = marker;
    });

    if (data.polyline && pathCoords.length >= 2) {
      polylineObj = L.polyline(pathCoords, {
        weight: 3,
        opacity: 0.7,
        color: getComputedStyle(container).getPropertyValue("--mta-accent") || "#0d6efd",
        dashArray: "4 6"
      }).addTo(map);
    }

    if (isFinite(data.center_latitude) && isFinite(data.center_longitude)) {
      map.setView([data.center_latitude, data.center_longitude], data.zoom || 13);
    } else if (pathCoords.length === 1) {
      map.setView(pathCoords[0], 13);
    } else {
      map.fitBounds(pathCoords, { padding: [32, 32] });
    }

    if (singleMarker) singleMarker.openPopup();
  }

  renderPoints(payload);

  const resizeObserver = new ResizeObserver(() => {
    map.invalidateSize();
  });
  resizeObserver.observe(container);

  return {
    destroy() {
      container.removeEventListener('click', handleMapClick);
      resizeObserver.disconnect();
      map.remove();
      if (mapEl.parentNode === container) container.removeChild(mapEl);
    },
    update(newData) { renderPoints(newData); },
    invalidate() { map.invalidateSize(); }
  };
}
